import { inject, injectable } from "inversify";
import type { IUserStore } from "@whatslly/ui-components/services/UserStore";
import { InjectUserStore } from "@whatslly/ui-components/services/UserStore";
import { UrlStore } from "../../../../services/urlStore";
import type { IOrganizationStore } from "../../../../services/IOrganizationStore";
import { InjectOrganizationStore } from "../../../../services/IOrganizationStore";
import { action, computed, makeObservable, observable, runInAction } from "mobx";
import { deleteJSON, getJSON, postJSON, putJSON } from "@whatslly/commons/apiUtils";
import { modalService } from "@whatslly/ui-components/modals";
import { TFunction } from "i18next";
import Toast from "@whatslly/ui-components/v2/Toaster/Toast";
import { FrontLogger } from "@whatslly/commons/frontLogger/frontLogger";
import { hasPermission } from "@whatslly/commons/platformPermissions";
import { UserPermissions } from "@whatslly/commons/granularPermissions";
import { AssignUserToPhoneModal } from "./AssignUserToPhoneModal/AssignUserToPhoneModal";
import type { TwilioAccount } from "@whatslly/types/twilio/TwilioGetPhones";
import { TwilioPhone, TwilioGetPhonesResponse } from "@whatslly/types/twilio/TwilioGetPhones";
import { TwilioAssignPhoneToUserBody, TwilioAssignPhoneToUserResponse } from "@whatslly/types/twilio/TwilioAssignPhoneToUser";
import { PurchacePhoneModal } from "./PurchacePhoneModal/PurchacePhoneModal";
import { RenamePhoneModal } from "./PurchacePhoneModal/RenamePhoneModal";
import { ConfirmModal } from "@whatslly/ui-components/modals/ConfirmModal";
import { TwilioConnectModal } from "./ConnectModal/TwilioConnectModal";
import type { TwilioCreateSubaccountResponseType } from "@whatslly/types/twilio/TwilioSubaccounts";
import type { TwilioCloseSubaccountResponseType } from "@whatslly/types/twilio/TwilioCloseSubaccount";
import { parseOrReturnError } from "@whatslly/commons/parseOrReturnError";
import { AskTuvisAccountManagerToConnectModal } from "./AskTuvisAccountManagerToConnectModal/AskTuvisAccountManagerToConnectModal";
import React from "react";
import { Text } from "@whatslly/ui-components/Text";
import styles from "./TwilioSettingsPage.module.scss";

@injectable()
export class TwilioSettingsStore {
  isInitialized = false;
  _phones: TwilioPhone[] = [];
  _accounts: TwilioAccount[] = [];
  connected = false;
  phonesUpdating = false;
  disableCreateSubaccountButton = false;
  constructor(
    @inject(InjectUserStore) private readonly userStore: IUserStore,
    @inject(UrlStore) private readonly urlStore: UrlStore,
    @inject(InjectOrganizationStore) private readonly orgStore: IOrganizationStore,
    @inject(FrontLogger) private readonly logger: FrontLogger
  ) {
    makeObservable(this, {
      phonesUpdating: observable,
      isInitialized: observable,
      searchPhoneTerm: computed,
      setSearchPhoneTerm: action,
      searchTerm: computed,
      setSearchTerm: action,
      connectToTwilio: action.bound,
      _phones: observable.shallow,
      _accounts: observable.shallow,
      loadPhones: action.bound,
      filteredPhones: computed,
      filteredAccounts: computed,
      connected: observable,
      isReadOnlySettingsUser: computed,
      assignPhoneToUser: action.bound,
      disableCreateSubaccountButton: observable,
    });
  }

  get isReadOnlySettingsUser() {
    return !hasPermission(this.userStore.user, UserPermissions["twilio.update_settings"]);
  }

  async updatePhoneUsers({
    sid,
    sms,
    voice,
    addUserIds,
    removeUserIds,
    t,
  }: {
    sid: string;
    sms: boolean;
    voice: boolean;
    addUserIds: string[];
    removeUserIds: string[];
    t: TFunction;
  }) {
    const body: TwilioAssignPhoneToUserBody = { orgId: (await this.getOrgId()) ?? "", addUserIds, removeUserIds, sms, voice };
    runInAction(() => {
      this.phonesUpdating = true;
    });
    const response = await putJSON({
      urlSuffix: `/integrations/twilio/phone/${sid}/users`,
      etoken: this.userStore.etoken ?? "",
      pod: await this.orgStore.wPod,
      body: JSON.stringify(body),
    }).then((res) =>
      parseOrReturnError<TwilioAssignPhoneToUserResponse>(res, this.logger).catch(() => ({ success: false }) as TwilioAssignPhoneToUserResponse)
    );

    if (response.success) {
      modalService.openModal(Toast, { text: t("whatsapp_business_api_settings_page.settings_saved_successfully") });
      this.loadPhones();
      return;
    }
    modalService.openModal(Toast, { text: t("whatsapp_business_api_settings_page.settings_saved_failed"), type: "error" });
  }
  async assignPhoneToUser(
    sid: string,
    initUsers: { allowedCapabilities: { sms: boolean; voice: boolean }; userId: string }[],
    t: TFunction,
    capabilities: { mms: boolean; sms: boolean; voice: boolean }
  ) {
    const res = await modalService.openModal(AssignUserToPhoneModal, { initUsers, capabilities }).promise;
    if (!res) {
      return;
    }

    await this.updatePhoneUsers({
      sid,
      sms: res.sms,
      voice: res.voice,
      addUserIds: res.selectedUserIds.filter((userId) => initUsers.filter((user) => user.userId === userId)),
      removeUserIds: initUsers.filter((user) => !res.selectedUserIds.includes(user.userId)).map((user) => user.userId),
      t,
    });
  }

  get filteredPhones() {
    if (!this.searchPhoneTerm) {
      return this._phones;
    }
    const term = this.searchPhoneTerm.trim().toLowerCase();
    const termNumbers = term.replace(/\D/g, "");
    if (!termNumbers) {
      return [];
    }
    return this._phones.filter((phoneData) => phoneData.phone?.replace(/\D/g, "").includes(termNumbers));
  }

  get filteredAccounts() {
    if (!this.searchTerm) {
      return this._accounts;
    }
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) {
      return this._accounts;
    }
    return this._accounts.filter((account) => account.AccountSid.toLowerCase().includes(term));
  }

  async getOrgId() {
    const org = await this.orgStore.selectedOrg;
    return org?.value ?? this.userStore.user?.organizationId;
  }

  async loadPhones() {
    const orgId = await this.getOrgId();
    const authorized = hasPermission(this.userStore.user, UserPermissions["twilio.update_settings"] | UserPermissions["twilio.view_settings"]);
    if (!orgId || !authorized) {
      return;
    }
    runInAction(() => {
      this.phonesUpdating = true;
    });
    const data = await getJSON({
      urlSuffix: `/integrations/twilio/phones?orgId=${orgId}`,
      etoken: this.userStore.etoken ?? "",
      pod: await this.orgStore.wPod,
    }).then((res) => parseOrReturnError<TwilioGetPhonesResponse>(res, this.logger).catch(() => ({ success: false }) as TwilioGetPhonesResponse));

    runInAction(() => {
      this._phones = data.success ? data.phones : [];
      this._accounts = data.success ? data.accounts : [];
      this.connected = data.success ? data.connected : false;
      this.isInitialized = true;
      this.phonesUpdating = false;
    });
  }

  get searchTerm() {
    return this.urlStore.searchParams?.get("twilio_search") ?? "";
  }

  setSearchTerm(searchTerm: string) {
    this.urlStore.setSearchParams({ twilio_search: searchTerm });
  }

  get searchPhoneTerm() {
    return this.urlStore.searchParams?.get("phone_search") ?? "";
  }

  setSearchPhoneTerm(searchPhoneTerm: string) {
    this.urlStore.setSearchParams({ phone_search: searchPhoneTerm });
  }

  async connectToTwilio() {
    const auth = await modalService.openModal(TwilioConnectModal).promise;
    if (!auth) {
      return;
    }
    const orgId = await this.getOrgId();
    await postJSON({
      urlSuffix: `/integrations/twilio/connect`,
      body: JSON.stringify({ ApiKey: auth.apiKey, ApiSecret: auth.apiSecret, AccountSid: auth.accountSid, AuthToken: auth.authToken, orgId }),
      etoken: this.userStore.etoken ?? "",
      pod: await this.orgStore.wPod,
    });
    this.loadPhones();
  }

  async createSubaccount(t: TFunction) {
    if (!this.userStore.user?.isSuperAdmin) {
      modalService.openModal(AskTuvisAccountManagerToConnectModal);
      return;
    }
    runInAction(() => {
      this.disableCreateSubaccountButton = true;
    });
    const loadingToast = modalService.openModal(Toast, { text: t("twilio_settings_page.create_account_loading"), timeout: 999999 });
    try {
      const orgId = await this.getOrgId();
      const res = await postJSON({
        urlSuffix: `/integrations/twilio/subaccounts`,
        etoken: this.userStore.etoken ?? "",
        pod: await this.orgStore.wPod,
        body: JSON.stringify({ orgId }),
      }).then((res) => parseOrReturnError<TwilioCreateSubaccountResponseType>(res, this.logger));
      loadingToast.close(undefined);
      runInAction(() => {
        this.connected = true;
        this.loadPhones();
      });
    } catch (err) {
      modalService.openModal(Toast, { text: t("twilio_settings_page.create_account_error"), type: "error" });
    } finally {
      runInAction(() => {
        this.disableCreateSubaccountButton = false;
      });
      loadingToast.close(undefined);
    }
  }

  async disconnect(account: TwilioAccount) {
    runInAction(() => {
      this.phonesUpdating = true;
    });
    await getJSON({
      urlSuffix: `/integrations/twilio/disconnect?AccountSid=${account.AccountSid}`,
      etoken: this.userStore.etoken ?? "",
      pod: await this.orgStore.wPod,
    });
    this.loadPhones();
  }

  async purchasePhone() {
    const accounts = this._accounts;
    const result = await modalService.openModal(PurchacePhoneModal, { accounts }).promise;
    if (result) {
      this.loadPhones();
    }
  }

  async closeSubAccountAccount(t: TFunction) {
    const orgId = await this.getOrgId();
    if (!orgId) {
      return;
    }

    const confirm = await modalService.openModal(ConfirmModal, {
      title: t("twilio_settings_page.close_account_modal.title"),
      body: (
        <div className={styles.modalContent}>
          <Text>{t("twilio_settings_page.close_account_modal.description")}</Text>
          <Text>{t("twilio_settings_page.close_account_modal.may_result_1")}</Text>
          <Text>{t("twilio_settings_page.close_account_modal.may_result_2")}</Text>
          <Text>{t("twilio_settings_page.close_account_modal.may_result_3")}</Text>
          <Text>{t("twilio_settings_page.close_account_modal.may_result_4")}</Text>
        </div>
      ),
    }).promise;
    if (!confirm?.result) {
      return;
    }

    const loadingToast = modalService.openModal(Toast, { text: t("twilio_settings_page.close_account_loading"), timeout: 999999 });
    try {
      await postJSON({
        urlSuffix: `/integrations/twilio/close`,
        etoken: this.userStore.etoken ?? "",
        pod: await this.orgStore.wPod,
        body: JSON.stringify({ orgId, AccountSid: this._accounts[0].AccountSid }),
      }).then((res) => parseOrReturnError<TwilioCloseSubaccountResponseType>(res, this.logger));
    } catch (err) {
      modalService.openModal(Toast, { text: t("twilio_settings_page.close_account_error"), type: "error" });
    } finally {
      loadingToast.close(undefined);
    }
  }

  async updatePhone(phone: TwilioPhone, rename = true, t: TFunction) {
    const orgId = await this.getOrgId();
    if (!orgId) {
      return;
    }
    let name = phone.name;
    if (rename) {
      const res = await modalService.openModal(RenamePhoneModal, { name }).promise;
      if (!res?.name) {
        return;
      }
      name = res.name;
    }
    await postJSON({
      urlSuffix: `/integrations/twilio/phones/${phone.accountSid}`,
      etoken: this.userStore.etoken ?? "",
      pod: await this.orgStore.wPod,
      body: JSON.stringify({ friendlyName: name, phoneNumberSid: phone.id, orgId }),
    });
    modalService.openModal(Toast, { text: t("whatsapp_business_api_settings_page.settings_saved_successfully") });
    this.loadPhones();
  }

  async releasePhone(t: TFunction, phone: TwilioPhone) {
    const orgId = await this.getOrgId();
    const confirm = await modalService.openModal(ConfirmModal, {
      title: t("twilio_settings_page.purchase_phone.confirm_release"),
      body: t("twilio_settings_page.purchase_phone.confirm_release_body"),
    }).promise;
    if (!confirm?.result || !orgId) {
      return;
    }
    await deleteJSON({
      urlSuffix: `/integrations/twilio/phones/${phone.accountSid}`,
      etoken: this.userStore.etoken ?? "",
      pod: await this.orgStore.wPod,
      body: JSON.stringify({ phoneNumberSid: phone.id, orgId }),
    });
    this.loadPhones();
  }
}
